package xim.poc.game.configuration.v0.tactics

import xim.math.Vector2f
import xim.poc.MenuStacks
import xim.poc.TextAlignment
import xim.poc.TextDirection
import xim.poc.UiElementHelper
import xim.poc.game.*
import xim.poc.game.CombatStat.*
import xim.poc.game.configuration.v0.CombatBonusAggregator
import xim.poc.game.configuration.v0.GameV0
import xim.poc.game.configuration.v0.TacticsUnit
import xim.poc.game.configuration.v0.tactics.TacticsZoneInstance.Companion.getCurrentMatch
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsBehaviorController
import xim.poc.gl.PortraitBufferManager
import xim.poc.ui.ShiftJis
import xim.resource.UiElement
import xim.resource.UiFlipMode
import kotlin.math.roundToInt

object TacticsUnitDescription {

    fun drawUnitDescription(unit: TacticsUnit, type: ActorType, jobId: TacticsJobId = unit.currentJob, state: ActorState? = null) {
        updatePortraitDummy(unit, jobId)

        val jobLevel = unit.jobLevels[jobId] ?: JobLevel(exp = 0, level = 1)
        val jobConfig = TacticsJobConfigs[jobId]

        val nameColor = if (type == ActorType.Enemy) { ShiftJis.colorInvalid } else { ShiftJis.colorItem }
        val nameString = "${nameColor}${unit.name}${ShiftJis.colorClear}"

        val unitLevelColor = coloredLevel(unit.unitLevel)
        val unitString = "Unit ${unitLevelColor}Lv.${unit.unitLevel.level}${ShiftJis.colorClear}"

        val message = "$nameString\n$unitString\n\n\n\n\n\n"
        val instinctLength = if (unit.instincts.isEmpty()) { 0 } else { unit.instincts.size }

        val descriptionLines = message.split("\n").count() + instinctLength

        val menuName = when {
            descriptionLines == 3 -> "menu    iteminfo"
            descriptionLines <= 9 -> "menu    item${descriptionLines}inf"
            else -> "menu    item${descriptionLines}in"
        }

        val menuPosition = UiElementHelper.drawMenu(menuName, menuStacks = MenuStacks.LogStack) ?: return
        UiElementHelper.drawString(message, offset = menuPosition + Vector2f(x = 46f, y = 8f))

        val powerRank = unit.getPowerRank()
        UiElementHelper.drawUiElement("menu    keytops3", powerRank.iconIndex, menuPosition + Vector2f(x = 115f, y = 23f))

        val xpColor = if (unit.isMaxUnitLevel() && unit.unitLevel.exp == 99) { ShiftJis.colorKey } else { ShiftJis.colorClear }
        UiElementHelper.drawString("XP:", offset = menuPosition + Vector2f(200f, 24f))
        UiElementHelper.drawString("${xpColor}${unit.unitLevel.exp}/100", offset = menuPosition + Vector2f(280f, 24f), alignment = TextAlignment.Right)

        val jpColor = if (unit.isMaxJobLevel() && jobLevel.exp == 99) { ShiftJis.colorKey } else { ShiftJis.colorClear }
        UiElementHelper.drawString("JP: ", offset = menuPosition + Vector2f(200f, 40f))
        UiElementHelper.drawString("${jpColor}${jobLevel.exp}/100", offset = menuPosition + Vector2f(280f, 40f), alignment = TextAlignment.Right)

        val jobLevelColor = coloredLevel(jobLevel)
        val jobString = UiElementHelper.formatString("${jobConfig.displayName} ${jobLevelColor}Lv.${jobLevel.level}${ShiftJis.colorClear}", textDirection = TextDirection.TopToBottom, maxWidth = 999)
        UiElementHelper.drawFormattedString(jobString, offset = menuPosition + Vector2f(x = 46f, y = 40f))

        val baseStats = (state?.behaviorController as? TacticsBehaviorController)?.getCombatStats() ?: unit.getCombatStats(jobId)
        val stats = state?.combatStats ?: baseStats

        val ranks = unit.getCombatStatRanks(jobId)

        val currentHpDisplay = state?.getHp() ?: stats.maxHp
        val hpString = UiElementHelper.formatString("${currentHpDisplay}/${s(maxHp, baseStats, stats)}", maxWidth = 1000, textDirection = TextDirection.TopToBottom)

        UiElementHelper.drawString("HP\nSTR\nVIT\nDEX", offset = menuPosition + Vector2f(x = 46f, y = 56f))
        UiElementHelper.drawFormattedString(hpString, offset = menuPosition + Vector2f(x = 100f, y = 56f))
        UiElementHelper.drawUiElement("menu    keytops3", ranks.maxHp.iconIndex, menuPosition + Vector2f(x = 104f + hpString.width, y = 55f))

        UiElementHelper.drawString(s(str, baseStats, stats), offset = menuPosition + Vector2f(x = 150f, y = 40f + 32f), alignment = TextAlignment.Right)
        UiElementHelper.drawString(s(vit, baseStats, stats), offset = menuPosition + Vector2f(x = 150f, y = 40f + 48f), alignment = TextAlignment.Right)
        UiElementHelper.drawString(s(dex, baseStats, stats), offset = menuPosition + Vector2f(x = 150f, y = 40f + 64f), alignment = TextAlignment.Right)

        ToolTipHelper.addToolTip(menuPosition + Vector2f(x = 46f, y = 40f + 32f), Vector2f(40f, 16f)) { "STR:\nUsed as the potency stat for all physical abilities, and various magical abilities." }
        ToolTipHelper.addToolTip(menuPosition + Vector2f(x = 46f, y = 40f + 48f), Vector2f(40f, 16f)) { "VIT:\nUsed as the defense stat for STR-based, DEX-based, and AGI-based abilities." }
        ToolTipHelper.addToolTip(menuPosition + Vector2f(x = 46f, y = 40f + 64f), Vector2f(40f, 16f)) { "DEX:\nIncreases chance of landing critical hits.\nIncreases critical hit damage." }

        UiElementHelper.drawUiElement("menu    keytops3", ranks.str.iconIndex, menuPosition + Vector2f(x = 154f, y = 39f + 32))
        UiElementHelper.drawUiElement("menu    keytops3", ranks.vit.iconIndex, menuPosition + Vector2f(x = 154f, y = 39f + 48))
        UiElementHelper.drawUiElement("menu    keytops3", ranks.dex.iconIndex, menuPosition + Vector2f(x = 154f, y = 39f + 64))

        UiElementHelper.drawString("\nINT\nMND\nAGI", offset = menuPosition + Vector2f(x = 200f, y = 56f))
        UiElementHelper.drawString(s(int, baseStats, stats), offset = menuPosition + Vector2f(x = 320f, y = 40f + 32f), alignment = TextAlignment.Right)
        UiElementHelper.drawString(s(mnd, baseStats, stats), offset = menuPosition + Vector2f(x = 320f, y = 40f + 48f), alignment = TextAlignment.Right)
        UiElementHelper.drawString(s(agi, baseStats, stats), offset = menuPosition + Vector2f(x = 320f, y = 40f + 64f), alignment = TextAlignment.Right)

        ToolTipHelper.addToolTip(menuPosition + Vector2f(x = 200f, y = 40f + 32f), Vector2f(80f, 16f)) { "INT:\nUsed as the potency stat for various magical abilities.\nUsed as the defense stat for INT-based abilities." }
        ToolTipHelper.addToolTip(menuPosition + Vector2f(x = 200f, y = 40f + 48f), Vector2f(80f, 16f)) { "MND:\nUsed as the potency stat for various magical abilities.\nUsed as the defense stat for MND-based abilities." }
        ToolTipHelper.addToolTip(menuPosition + Vector2f(x = 200f, y = 40f + 64f), Vector2f(80f, 16f)) { "AGI:\nUsed to determine the amount of TP received when attacking or being attacked.\nReduces chance of enemies' critical hits." }

        UiElementHelper.drawUiElement("menu    keytops3", ranks.int.iconIndex, menuPosition + Vector2f(x = 324f, y = 39f + 32f))
        UiElementHelper.drawUiElement("menu    keytops3", ranks.mnd.iconIndex, menuPosition + Vector2f(x = 324f, y = 39f + 48f))
        UiElementHelper.drawUiElement("menu    keytops3", ranks.agi.iconIndex, menuPosition + Vector2f(x = 324f, y = 39f + 64f))

        val elementalStringBuilder = StringBuilder()

        val resistanceTable = if (state != null) {
            CombatBonusAggregator[state].elementalResistance
        } else {
            val multiplier = getCurrentMatch()?.config?.getJobResistMultiplier() ?: 1f
            val baseResists = jobResistanceTables[jobConfig.element] ?: emptyMap()
            baseResists.mapValues { (it.value * multiplier).roundToInt() }
        }

        for ((element, resistance) in resistanceTable) {
            val color = if (resistance >= 0) { ShiftJis.colorItem } else { ShiftJis.colorInvalid }
            elementalStringBuilder.append("$color${resistance}%${ShiftJis.colorClear}${element.toCharacter()}    ")
        }

        if (resistanceTable.isNotEmpty()) {
            UiElementHelper.drawString("Resistance: $elementalStringBuilder",menuPosition + Vector2f(x = 46f, y = 120f))
        }

        drawInstinct(unit, offset = menuPosition + Vector2f(x = 46f, y = 136f))

        val photoTarget = state ?: getPortraitDummy() ?: return
        val portrait = PortraitBufferManager.getTakenPhoto(photoTarget.id) ?: return
        val portraitElement = UiElement.basicSquare(portrait.name, size = 32, uvHeight = 64, uvWidth = 64, flipMode = UiFlipMode.Vertical)

        val internalQuality = state?.getUnitConfig()?.internalQuality ?: 0
        UiElementHelper.drawCustomBorder(position = menuPosition + Vector2f(8f, 12f), customBorder = internalQuality)
        UiElementHelper.drawUiElement(portraitElement, position = menuPosition + Vector2f(10f, 12f))
    }

    private fun drawInstinct(unit: TacticsUnit, offset: Vector2f) {
        if (unit.instincts.isEmpty()) { return }
        val instincts = unit.instincts.joinToString("\n") { "  ${ShiftJis.colorInfo}${it.display()}${ShiftJis.colorClear}" }
        UiElementHelper.drawString(instincts, offset)

        for (i in unit.instincts.indices) {
            val instinct = unit.instincts[i]
            ToolTipHelper.addToolTip(offset + Vector2f(0f, 16f * i - 4), Vector2f(200f, 16f)) { instinct.toolTip() }
        }
    }

    private fun s(combatStat: CombatStat, baseStats: CombatStats, currentStats: CombatStats): String {
        val currentStat = currentStats[combatStat]
        val baseStat = baseStats[combatStat]

        val color = if (currentStat < baseStat) {
            ShiftJis.colorInvalid
        } else if (currentStat > baseStat) {
            ShiftJis.colorItem
        } else {
            ShiftJis.colorClear
        }

        return "$color${currentStat}${ShiftJis.colorClear}"
    }

    private fun coloredLevel(jobLevel: JobLevel): Char {
        return if (jobLevel.level >= TacticsState.getMaxLevel()) { ShiftJis.colorKey } else { ShiftJis.colorClear }
    }

    private fun updatePortraitDummy(unit: TacticsUnit, jobId: TacticsJobId) {
        val unitConfig = TacticsJobConfigs[jobId]
        val dummy = getPortraitDummy() ?: return
        dummy.setBaseLook(unitConfig.look)
        PortraitBufferManager.submitPhotoRequest(dummy.id, transient = true)
    }

    private fun getPortraitDummy(): ActorState? {
        return GameV0.getCurrentTacticsZoneInstance()?.portraitDummy?.promise?.resolveIfReady()
    }

}